/**
 * Disaster layer factory for GDACS alerts and weather events.
 * ScatterplotLayer for alert dots, icon markers for volcanoes,
 * PathLayer for cyclone tracks.
 */

import { ScatterplotLayer, IconLayer, PathLayer, PolygonLayer } from '@deck.gl/layers';
import { PathStyleExtension } from '@deck.gl/extensions';
import type { Layer } from '@deck.gl/core';
import type { NormalizedEvent } from '@/sources/base-source';
import { createSvgIcon } from './icon-layer';

/** Layer IDs handled by the disaster renderer. */
export const NATURAL_HAZARD_LAYER_IDS = new Set(['gdacs', 'volcanoes', 'tropical-cyclones']);

// ── Wind speed color scale (7-tier, matching WorldMonitor TC_WIND_COLORS) ──

/** Wind speed thresholds and colors: [minKnots, RGBA]. */
const TC_WIND_COLORS: [number, [number, number, number, number]][] = [
  [137, [255, 96, 96, 200]],     // Cat5 — red
  [113, [255, 140, 0, 200]],     // Cat4 — orange
  [96,  [255, 140, 0, 200]],     // Cat3 — orange
  [83,  [255, 231, 117, 200]],   // Cat2 — yellow
  [64,  [255, 231, 117, 200]],   // Cat1 — yellow
  [34,  [94, 186, 255, 200]],    // TS — blue
  [0,   [160, 160, 160, 160]],   // TD — gray
];

/** Look up wind color by knots — matching WorldMonitor. */
function windColor(kt: number): [number, number, number, number] {
  for (const [threshold, color] of TC_WIND_COLORS) {
    if (kt >= threshold) return color;
  }
  return [160, 160, 160, 160];
}

/**
 * Normalize the GDACS alert level: the feed emits "Red"/"Orange"/"Green"
 * (capitalized), and an empty level falls through to the event severity.
 */
function getAlertLevel(event: NormalizedEvent): string {
  const raw = event.raw as Record<string, unknown> | undefined;
  const level = typeof raw?.alertLevel === 'string' ? raw.alertLevel.trim().toLowerCase() : '';
  return level || event.severity || '';
}

/**
 * GDACS alert colors matching WM (DeckGLMap.ts:3221-3225): the whole ramp
 * stays in the red/orange hazard family and alpha itself carries severity.
 */
export function getAlertColor(event: NormalizedEvent): [number, number, number, number] {
  switch (getAlertLevel(event)) {
    case 'critical':
    case 'red': return [255, 0, 0, 220];
    case 'high':
    case 'orange': return [255, 140, 0, 200];
    default: return [255, 150, 50, 180];
  }
}

/** GDACS alert radius in meters (WM DeckGLMap.ts:3220). */
function getAlertRadius(event: NormalizedEvent): number {
  switch (getAlertLevel(event)) {
    case 'critical':
    case 'red': return 20_000;
    case 'high':
    case 'orange': return 15_000;
    default: return 8_000;
  }
}

/** Create layers for GDACS disaster alerts. */
export function createGdacsLayers(
  events: NormalizedEvent[],
  _zoom: number,
): Layer[] {
  const layers: Layer[] = [];

  // Main alert dots — meters with pixel clamps so markers grow on zoom-in
  // (WM DeckGLMap.ts:3220-3227); no layer opacity, alpha lives in the fill.
  layers.push(new ScatterplotLayer<NormalizedEvent>({
    id: 'disaster-gdacs-dots',
    data: events,
    pickable: true,
    filled: true,
    radiusUnits: 'meters' as const,
    radiusMinPixels: 5,
    radiusMaxPixels: 18,
    getPosition: (d) => d.location ? [d.location.lng, d.location.lat] : [0, 0],
    getFillColor: (d) => getAlertColor(d),
    getRadius: (d) => getAlertRadius(d),
  }));

  // Cyclone tracks — events with path data in raw
  // (The old purple raw.trackPath PathLayer was dead code — no source ever
  // produced trackPath, and WM colors storm tracks by wind speed via the
  // tropical-cyclone stack, not a flat purple line.)
  return layers;
}

/** Create layers for volcanic activity. */
export function createVolcanoLayers(
  events: NormalizedEvent[],
  zoom: number,
): Layer[] {
  const opacity = Math.min(1, 0.3 + zoom * 0.1);

  return [
    new IconLayer<NormalizedEvent>({
      id: 'disaster-volcanoes',
      data: events,
      pickable: true,
      getPosition: (d) => d.location ? [d.location.lng, d.location.lat] : [0, 0],
      getIcon: (d) => {
        const [r, g, b] = getAlertColor(d);
        return {
          url: createSvgIcon('mountain', [r, g, b]),
          width: 48,
          height: 48,
          anchorY: 48,
        };
      },
      getSize: zoom < 5 ? 22 : 30,
      sizeUnits: 'pixels' as const,
      opacity,
    }),
  ];
}

// ── Tropical Cyclone Layers (Matching WorldMonitor) ──────────────────────

/** A single track point with wind speed and position. */
interface TrackPoint {
  lng: number;
  lat: number;
  windKt: number;
}

/** Forecast cone polygon. */
interface ForecastCone {
  polygon: [number, number][];
}

/** Path segment for wind-colored rendering. */
interface WindSegment {
  path: [number, number][];
  color: [number, number, number, number];
}

/** A forecast wind-radii band ring with its wind threshold. */
interface WindRadiiBand {
  thresholdKt: number;
  polygon: [number, number][];
}

/**
 * Whether an event actually carries cyclone data. Generic 'weather' events
 * (NWS alerts, EONET drought/dust/...) route to this layer too — without
 * this gate every routine alert would render a gray TD-colored storm
 * center from its location alone.
 */
export function isStormEvent(raw: Record<string, unknown>): boolean {
  return (
    typeof raw.windKt === 'number' ||
    typeof raw.stormName === 'string' ||
    Array.isArray(raw.pastTrack) ||
    Array.isArray(raw.forecastTrack) ||
    Array.isArray(raw.forecastCone) ||
    Array.isArray(raw.windRadii)
  );
}

/** Extract storm data from cyclone events. */
function extractStormData(events: NormalizedEvent[]): {
  pastTracks: WindSegment[];
  forecastTracks: [number, number][][];
  cones: ForecastCone[];
  centers: TrackPoint[];
  windRadii: WindRadiiBand[];
} {
  const pastTracks: WindSegment[] = [];
  const forecastTracks: [number, number][][] = [];
  const cones: ForecastCone[] = [];
  const centers: TrackPoint[] = [];
  const windRadii: WindRadiiBand[] = [];

  for (const event of events) {
    const raw = event.raw as Record<string, unknown> | undefined;
    if (!raw || !isStormEvent(raw)) continue;

    // Past track points with wind speeds
    const past = raw.pastTrack as TrackPoint[] | undefined;
    if (Array.isArray(past) && past.length >= 2) {
      // Break into per-segment color paths
      for (let i = 0; i < past.length - 1; i++) {
        const p1 = past[i];
        const p2 = past[i + 1];
        pastTracks.push({
          path: [[p1.lng, p1.lat], [p2.lng, p2.lat]],
          color: windColor(p2.windKt ?? 0),
        });
      }
    }

    // Forecast track
    const forecast = raw.forecastTrack as [number, number][] | undefined;
    if (Array.isArray(forecast) && forecast.length >= 2) {
      forecastTracks.push(forecast);
    }

    // Forecast cone polygon
    const cone = raw.forecastCone as [number, number][] | undefined;
    if (Array.isArray(cone) && cone.length >= 3) {
      cones.push({ polygon: cone });
    }

    // Current center position
    if (event.location) {
      const wkt = typeof raw.windKt === 'number' ? raw.windKt : 0;
      centers.push({ lng: event.location.lng, lat: event.location.lat, windKt: wkt });
    }

    // Forecast wind-radii bands (34/50/64 kt damage-swath shells)
    const bands = raw.windRadii as {
      thresholdKt?: number;
      geometryKind?: string;
      polygons?: [number, number][][][];
    }[] | undefined;
    if (Array.isArray(bands)) {
      for (const band of bands) {
        if (band.geometryKind && band.geometryKind !== 'forecast-wind-radii') continue;
        if (typeof band.thresholdKt !== 'number' || !Array.isArray(band.polygons)) continue;
        for (const polygon of band.polygons) {
          const ring = polygon?.[0];
          if (Array.isArray(ring) && ring.length >= 3) {
            windRadii.push({ thresholdKt: band.thresholdKt, polygon: ring });
          }
        }
      }
    }
  }

  return { pastTracks, forecastTracks, cones, centers, windRadii };
}

/** Wind-radii band fill — WM DeckGLMap.ts:3276-3281, alpha steps down with
 * the threshold so nested bands read as concentric intensity shells. */
export function getWindRadiiFill(thresholdKt: number): [number, number, number, number] {
  if (thresholdKt >= 64) return [180, 0, 0, 40];
  if (thresholdKt >= 50) return [220, 80, 0, 36];
  if (thresholdKt >= 34) return [220, 160, 0, 32];
  return [200, 200, 0, 28];
}

/**
 * Create deck.gl layers for tropical cyclone data — matching WorldMonitor.
 * Includes: forecast cone polygon, wind-colored past track, dashed forecast track,
 * and wind-colored storm center dots.
 */
export function createTropicalCycloneLayers(
  events: NormalizedEvent[],
  zoom: number,
): Layer[] {
  if (events.length === 0) return [];

  const opacity = Math.min(1, 0.3 + zoom * 0.1);
  const layers: Layer[] = [];
  const { pastTracks, forecastTracks, cones, centers, windRadii } = extractStormData(events);

  // 1. Forecast cone polygon — white semi-transparent
  if (cones.length > 0) {
    layers.push(new PolygonLayer<ForecastCone>({
      id: 'storm-cone',
      data: cones,
      pickable: false,
      filled: true,
      stroked: true,
      getPolygon: (d) => d.polygon,
      getFillColor: [255, 255, 255, 30],
      getLineColor: [255, 255, 255, 80],
      getLineWidth: 1,
      lineWidthMinPixels: 1,
      lineWidthUnits: 'pixels' as const,
      opacity,
    }));
  }

  // 1b. Forecast wind-radii bands — concentric damage-swath shells between
  // the cone and the tracks (WM DeckGLMap.ts:3254-3283)
  if (windRadii.length > 0) {
    layers.push(new PolygonLayer<WindRadiiBand>({
      id: 'storm-wind-radii',
      data: windRadii,
      pickable: true,
      filled: true,
      stroked: true,
      getPolygon: (d) => d.polygon,
      getFillColor: (d) => getWindRadiiFill(d.thresholdKt),
      getLineColor: [255, 180, 0, 120],
      lineWidthMinPixels: 1,
    }));
  }

  // 2. Past track — wind-speed colored segments, 3px width (no opacity
  // multiplier — WM renders the wind colors at full strength)
  if (pastTracks.length > 0) {
    layers.push(new PathLayer<WindSegment>({
      id: 'storm-past-track',
      data: pastTracks,
      pickable: true,
      getPath: (d) => d.path,
      getColor: (d) => d.color,
      getWidth: 3,
      widthUnits: 'pixels' as const,
    }));
  }

  // 3. Forecast track — dashed red line
  if (forecastTracks.length > 0) {
    const forecastData = forecastTracks.map((path, i) => ({ id: `fc-${i}`, path }));
    layers.push(new PathLayer<{ id: string; path: [number, number][] }>({
      id: 'storm-forecast-track',
      data: forecastData,
      pickable: false,
      getPath: (d) => d.path,
      getColor: [255, 100, 100, 200],
      getWidth: 2,
      widthUnits: 'pixels' as const,
      opacity: opacity * 0.8,
      // @ts-expect-error — getDashArray comes from PathStyleExtension
      getDashArray: [6, 4],
      dashJustified: true,
      extensions: [new PathStyleExtension({ dash: true })],
    }));
  }

  // 4. Storm centers — wind-colored dots with white outline
  if (centers.length > 0) {
    layers.push(new ScatterplotLayer<TrackPoint>({
      id: 'storm-centers',
      data: centers,
      pickable: true,
      filled: true,
      stroked: true,
      radiusMinPixels: 6,
      radiusMaxPixels: 20,
      getPosition: (d) => [d.lng, d.lat],
      getFillColor: (d) => windColor(d.windKt),
      getLineColor: [255, 255, 255, 200],
      lineWidthMinPixels: 2,
      getRadius: 15000,
      opacity,
    }));
  }

  return layers;
}
